/**
 * Auth login payload helpers (ported from cpanel
 * `src/features/auth/model/payload.ts` — logic 1:1).
 *
 * The masked phone input is stripped of mask chars before being sent as
 * `phone`; the password passes through untouched. Device identity is always
 * attached: it is what names a row on the sessions screen.
 */
import { getDeviceId, getDeviceName, WEB_PLATFORM } from './device-identity';

export interface LoginFormValues {
  phone: string;
  password: string;
}

/** Auth-service login body — base body + device identity. */
export interface CoreLoginPayload extends LoginFormValues {
  device_id?: string;
  device_name?: string;
  platform?: string;
}

/** `{ access_token, refresh_token }` — returned by both login and refresh. */
export interface TokenPair {
  access_token?: string;
  refresh_token?: string;
}

export interface AccessTokenPayload {
  phone?: string;
  role?: string;
  sub?: string;
}

/**
 * Strip mask placeholder/formatting chars from a masked phone string.
 * Mirrors cpanel: `[X() ]` plus any stray `x`/`X` placeholder fill, then the
 * caller keeps digits (and a leading `+`).
 */
export function stripPhoneMask(phone: string): string {
  return phone.replace(/[X() ]/g, '').replace(/x/gi, '');
}

/** Normalize a raw login input to E.164-ish form (`+` + digits). */
export function normalizePhone(phone: string): string {
  const stripped = stripPhoneMask(phone.trim());
  const plus = stripped.startsWith('+') ? '+' : '';
  const digits = stripped.replace(/\D/g, '');
  return digits ? `${plus}${digits}` : '';
}

export function buildLoginPayload(data: LoginFormValues): LoginFormValues {
  return {
    phone: normalizePhone(data.phone),
    password: data.password,
  };
}

/**
 * Full login body with device identity. `device_id` is the browser's
 * persisted UUID, `device_name` a best-effort vendor/OS label ("Lenovo", "HP",
 * "Windows PC · Chrome"), and `platform` is always `web`.
 */
export function buildCoreLoginPayload(data: LoginFormValues): CoreLoginPayload {
  return {
    ...buildLoginPayload(data),
    device_id: getDeviceId(),
    device_name: getDeviceName(),
    platform: WEB_PLATFORM,
  };
}

export function normalizeRole(role: string | undefined | null): string | undefined {
  return role?.toLowerCase() ?? undefined;
}

export function decodeAccessTokenPayload(
  token: string | undefined,
): AccessTokenPayload | undefined {
  const payload = token?.split('.')[1];

  if (!payload) return undefined;

  try {
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      Math.ceil(normalizedPayload.length / 4) * 4,
      '=',
    );
    return JSON.parse(atob(paddedPayload)) as AccessTokenPayload;
  } catch {
    return undefined;
  }
}
